import React from "react";
import { Alert, Button, List, Modal, Progress, Space, Tag, Typography } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, WarningOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

const { Title, Text } = Typography;

dayjs.extend(utc);
dayjs.extend(timezone);

const ProcessShipmentsProgressModal = ({
  visible,
  onClose,
  progress,
  currentLead,
  currentOperation,
  completedShipments,
  isComplete,
  summary,
}) => {
  const { t } = useTranslation();

  // Detect stock failures across completed shipments
  const stockFailures = Array.isArray(completedShipments)
    ? completedShipments.filter(
        (item) => item?.operations?.stock && item.operations.stock.attempted && !item.operations.stock.success,
      )
    : [];
  const hasStockFailures = stockFailures.length > 0;

  const downloadStockFailureLog = () => {
    try {
      const payload = stockFailures.map((item) => ({
        farmorderId: item.farmorderId,
        lead: item.lead?.companyName,
        deliveryDate: item.lead?.desiredDeliveryDate,
        status: item.status,
        stock: {
          error: item.operations?.stock?.error || null,
          response: item.operations?.stock?.response || null,
        },
        sooCool: {
          success: item.operations?.sooCool?.success,
          error: item.operations?.sooCool?.error || null,
          response: item.operations?.sooCool?.response || null,
        },
      }));

      const blob = new Blob(
        [
          JSON.stringify(
            {
              generatedAt: dayjs().tz("Europe/Amsterdam").format("YYYY-MM-DDTHH:mm:ssZ"),
              count: payload.length,
              failures: payload,
            },
            null,
            2,
          ),
        ],
        { type: "application/json" },
      );

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `shipment-stock-failures-${dayjs().format("YYYYMMDD-HHmmss")}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Failed to generate stock failure log", e);
    }
  };

  // Detect sooCool failures
  const sooCoolFailures = Array.isArray(completedShipments)
    ? completedShipments.filter(
        (item) => item?.operations?.sooCool && item.operations.sooCool.attempted && !item.operations.sooCool.success,
      )
    : [];
  const hasSooCoolFailures = sooCoolFailures.length > 0;

  const downloadSooCoolFailureLog = () => {
    try {
      const payload = sooCoolFailures.map((item) => ({
        farmorderId: item.farmorderId,
        lead: item.lead?.companyName,
        deliveryDate: item.lead?.desiredDeliveryDate,
        status: item.status,
        sooCool: {
          error: item.operations?.sooCool?.error || null,
          response: item.operations?.sooCool?.response || null,
        },
        stock: {
          success: item.operations?.stock?.success,
          error: item.operations?.stock?.error || null,
          response: item.operations?.stock?.response || null,
        },
      }));

      const blob = new Blob(
        [
          JSON.stringify(
            {
              generatedAt: dayjs().tz("Europe/Amsterdam").format("YYYY-MM-DDTHH:mm:ssZ"),
              count: payload.length,
              failures: payload,
            },
            null,
            2,
          ),
        ],
        { type: "application/json" },
      );

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `shipment-soocool-failures-${dayjs().format("YYYYMMDD-HHmmss")}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Failed to generate sooCool failure log", e);
    }
  };

  const getOperationDisplayName = (operation) => {
    const names = {
      firestore: t("creating_shipment"),
      stock: t("updating_stock"),
      sooCool: t("notifications.soocool_delivery_created"),
    };
    return names[operation] || operation;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "error":
        return <CloseCircleOutlined style={{ color: "#ff4d4f" }} />;
      case "partial":
        return <WarningOutlined style={{ color: "#faad14" }} />;
      case "processing":
        return <LoadingOutlined style={{ color: "#1890ff" }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "success";
      case "error":
        return "error";
      case "partial":
        return "warning";
      case "processing":
        return "processing";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "success":
        return t("success");
      case "error":
        return t("failed");
      case "partial":
        return t("partial");
      case "processing":
        return t("processing");
      default:
        return status;
    }
  };

  const renderOperationDetails = (operations) => {
    if (!operations) return null;

    return (
      <div style={{ marginTop: 8, fontSize: "12px" }}>
        {Object.entries(operations).map(([operation, result]) => {
          if (!result.attempted) return null;

          return (
            <div key={operation} style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {result.success ? (
                  <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "12px" }} />
                ) : (
                  <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: "12px" }} />
                )}
                <Text type={result.success ? "secondary" : "danger"} style={{ fontSize: "12px" }}>
                  {getOperationDisplayName(operation)}: {result.success ? t("success") : t("failed")}
                </Text>
              </div>

              {result.error && (
                <div style={{ marginLeft: 24, marginTop: 2 }}>
                  <Text type="danger" style={{ fontSize: "11px" }}>
                    ({result.error})
                  </Text>
                </div>
              )}

              {!result.success && result.response?.message && (
                <div style={{ marginLeft: 24, marginTop: 2 }}>
                  <Text type="danger" style={{ fontSize: "11px" }}>
                    {result.response.message}
                  </Text>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>
            {isComplete ? t("shipment_processing_complete") : t("processing_shipments")}
          </Title>
          <Tag color="processing">
            {progress.current} / {progress.total}
          </Tag>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={
        isComplete
          ? [
              <Button key="close" type="primary" onClick={onClose}>
                {t("close")}
              </Button>,
            ]
          : null
      }
      width={800}
      style={{ top: 20 }}
      closable={isComplete}
      maskClosable={false}
    >
      {/* Progress Bar */}
      <div style={{ marginBottom: 24 }}>
        <Progress
          percent={Math.round((progress.current / progress.total) * 100)}
          status={isComplete ? "success" : "active"}
          showInfo={true}
        />
      </div>

      {/* Current Lead Being Processed */}
      {!isComplete && currentLead && (
        <div
          style={{
            marginBottom: 24,
            padding: 16,
            backgroundColor: "#f5f5f5",
            borderRadius: 6,
            border: "1px solid #d9d9d9",
          }}
        >
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Text strong>{t("currently_processing")}:</Text>
            <Text style={{ fontSize: "16px" }}>{currentLead.companyName}</Text>
            <Text type="secondary">
              {t("delivery_date")}: {dayjs(currentLead.desiredDeliveryDate).format("dddd DD-MM-YYYY")}
            </Text>
            {currentOperation && (
              <Text type="secondary" style={{ fontStyle: "italic", color: "#1890ff" }}>
                {getOperationDisplayName(currentOperation)}...
              </Text>
            )}
          </Space>
        </div>
      )}

      {/* Summary when complete */}
      {isComplete && summary && (
        <div style={{ marginBottom: 24 }}>
          {summary.successCount > 0 && (
            <Alert
              message={`${summary.successCount} ${t("shipments_processed_successfully")}`}
              type="success"
              showIcon
              style={{ marginBottom: 8 }}
            />
          )}
          {summary.partialCount > 0 && (
            <Alert
              message={`${summary.partialCount} ${t("shipments_processed_with_warnings")}`}
              type="warning"
              showIcon
              style={{ marginBottom: 8 }}
            />
          )}
          {summary.errorCount > 0 && (
            <Alert
              message={`${summary.errorCount} ${t("shipments_failed_to_process")}`}
              type="error"
              showIcon
              style={{ marginBottom: 8 }}
            />
          )}

          {/* Prominent Stock Failure Alert with log download */}
          {hasStockFailures && (
            <Alert
              message={t("stock_update_failed")}
              description={t("some_shipments_stock_failed")}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
              action={
                <Button size="small" type="primary" onClick={downloadStockFailureLog}>
                  {t("download_error_log")}
                </Button>
              }
            />
          )}

          {/* Prominent SooCool Failure Alert with log download */}
          {hasSooCoolFailures && (
            <Alert
              message={t("soocool_delivery_failed")}
              description={t("some_shipments_soocool_failed")}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
              action={
                <Button size="small" type="primary" onClick={downloadSooCoolFailureLog}>
                  {t("download_error_log")}
                </Button>
              }
            />
          )}
        </div>
      )}

      {/* Shipment Status List */}
      <div>
        <Title level={5} style={{ marginBottom: 16 }}>
          {t("shipment_status")}
        </Title>

        <List
          dataSource={completedShipments}
          renderItem={(item, index) => (
            <List.Item
              style={{
                padding: "12px 0",
                borderBottom: index < completedShipments.length - 1 ? "1px solid #f0f0f0" : "none",
              }}
            >
              <List.Item.Meta
                avatar={getStatusIcon(item.status)}
                title={
                  <Space>
                    <Text strong>#{index + 1}</Text>
                    <Text>{item.lead.companyName}</Text>
                    <Tag color={getStatusColor(item.status)} size="small">
                      {getStatusText(item.status)}
                    </Tag>
                    {item?.operations?.stock?.attempted && !item?.operations?.stock?.success && (
                      <Tag color="error" size="small">
                        {t("stock_failed")}
                      </Tag>
                    )}
                    {item.farmorderId && (
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        ID: {item.farmorderId}
                      </Text>
                    )}
                  </Space>
                }
                description={
                  <div>
                    <Text type="secondary" style={{ fontSize: "13px" }}>
                      {dayjs(item.lead.desiredDeliveryDate).format("ddd DD-MM-YYYY")}
                    </Text>

                    {item.operations && renderOperationDetails(item.operations)}

                    {item.error && !item.operations && (
                      <div style={{ marginTop: 4 }}>
                        <Text type="danger" style={{ fontSize: "12px" }}>
                          {t("error")}: {item.error}
                        </Text>
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
          style={{
            maxHeight: 350,
            overflowY: "auto",
            border: "1px solid #f0f0f0",
            borderRadius: 6,
            padding: "8px 16px",
            backgroundColor: "#fafafa",
          }}
          locale={{
            emptyText: t("no_shipments_processed"),
          }}
        />
      </div>
    </Modal>
  );
};

export default ProcessShipmentsProgressModal;
